import React, { useMemo } from "react";
import { IoCheckmark, IoClose } from "react-icons/io5";
import { HiOutlineArrowUturnLeft, HiOutlineCheckBadge } from "react-icons/hi2";
import CustomButton from "components/common/Button/CustomButton";
import TimesheetAiAnalyzeButton from "../../TimeSheetDetailsSection/ai/components/TimesheetAiAnalyzeButton";

const TimesheetBulkToolbar = ({
  selectedCount = 0,
  setUpdatedAction,
  disabled,
  loading,
  bulkMode = "approve",
  onBulkUnapprove,
  unapproveModalOpen = false,
  showAiAnalyze = false,
  aiAnalyzeDisabled = true,
  aiAnalyzeLoading = false,
  onAnalyzeWithAi,
  bulkAnalyseButton = null,
}) => {
  const hasSelection = selectedCount > 1;
  const isUnapproveMode = bulkMode === "unapprove";
  const actionsDisabled =
    !hasSelection ||
    disabled ||
    loading ||
    (unapproveModalOpen && !loading);
  const showUnapproveLoader = loading && !unapproveModalOpen;

  const hintText = useMemo(() => {
    if (hasSelection) {
      return isUnapproveMode
        ? "Bulk unapprove mode — click a row to clear selection and open detail"
        : "Bulk mode — click a row to clear selection and open detail";
    }
    if (selectedCount === 1) {
      return "Uncheck to remove from bulk · row opens detail panel";
    }
    if (isUnapproveMode) {
      return "Select approved, rejected, or expired rows to bulk unapprove";
    }
    return "Row opens detail (click again to close) · checkboxes for bulk";
  }, [hasSelection, isUnapproveMode, selectedCount]);

  return (
    <div className="timesheet-bulk-toolbar">
      <div className="timesheet-bulk-toolbar__row">
        <span className="timesheet-bulk-toolbar__badge inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-[#F8FAFC] px-2.5 py-1 text-xs font-semibold text-[#64748B]">
          <HiOutlineCheckBadge className="h-3.5 w-3.5 shrink-0" />
          {selectedCount} selected
        </span>

        <p className="timesheet-bulk-toolbar__hint timesheet-bulk-toolbar__hint--inline">
          {hintText}
        </p>

        <div className="timesheet-bulk-toolbar__actions">
          {isUnapproveMode ? (
            <CustomButton
              type="button"
              variant="danger"
              label="Unapprove"
              Icon={
                !showUnapproveLoader ? (
                  <HiOutlineArrowUturnLeft className="h-4 w-4" />
                ) : null
              }
              showLoading={showUnapproveLoader}
              loaderCssClass="!text-red-800 !mr-0"
              isDisabled={actionsDisabled}
              clickHandler={onBulkUnapprove}
              cssClass={`timesheet-bulk-toolbar__btn timesheet-bulk-toolbar__btn--unapprove-danger${
                showUnapproveLoader ? " is-loading" : ""
              }${
                actionsDisabled && !showUnapproveLoader
                  ? " !cursor-not-allowed !opacity-40"
                  : ""
              }`}
            />
          ) : (
            <>
              {bulkAnalyseButton ? (
                <div className="timesheet-bulk-toolbar__ai-slot">
                  {bulkAnalyseButton}
                </div>
              ) : null}
              {showAiAnalyze ? (
                <div className="timesheet-bulk-toolbar__ai-slot">
                  <TimesheetAiAnalyzeButton
                    loading={aiAnalyzeLoading}
                    disabled={aiAnalyzeDisabled}
                    onClick={onAnalyzeWithAi}
                    compact
                  />
                </div>
              ) : null}
              <button
                type="button"
                disabled={actionsDisabled}
                onClick={() => setUpdatedAction("approved")}
                className={`timesheet-bulk-toolbar__btn bg-[#0DBBB3] text-white ${
                  actionsDisabled
                    ? "cursor-not-allowed opacity-40"
                    : "hover:opacity-90"
                }`}
              >
                <IoCheckmark className="h-4 w-4 shrink-0" />
                Approve
              </button>
              <button
                type="button"
                disabled={actionsDisabled}
                onClick={() => setUpdatedAction("rejected")}
                className={`timesheet-bulk-toolbar__btn border border-[#FC466C] bg-white text-[#FC466C] ${
                  actionsDisabled
                    ? "cursor-not-allowed opacity-40"
                    : "hover:bg-[#FEE9EE]"
                }`}
              >
                <IoClose className="h-4 w-4 shrink-0" />
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      <p className="timesheet-bulk-toolbar__hint timesheet-bulk-toolbar__hint--below">
        {hintText}
      </p>
    </div>
  );
};

export default TimesheetBulkToolbar;
